import { isResponseWithMetaInRoot, type DataFetcherFunction, type DataFetcherResponse, type DataFetcherSort } from '@/compiler/types';

interface FetchAllOptions<TData> {
    sorts?: DataFetcherSort[]
    filters?: string
    pageSize?: number
    onProgress?: (currentPage: number, totalPages: number, items: TData[]) => void
    onError?: (error: Error, page: number) => void
}

export default function useFetchAllFromPaginatedResource<TData>(
    fetchFn: DataFetcherFunction<TData>,
    options?: FetchAllOptions<TData>,
): () => Promise<TData[]> {
    return async (): Promise<TData[]> => {
        const pageSize = options?.pageSize ?? 100;
        const sorts = options?.sorts ?? [];
        const filters = options?.filters ?? '';
        const fetchPage = (page: number): Promise<DataFetcherResponse<TData>> => {
            const response = fetchFn(page, pageSize, sorts, filters);
            if (!options?.onError) return response;

            return response.catch((error: unknown) => {
                const errorInstance = error instanceof Error ? error : new Error(String(error));
                options.onError?.(errorInstance, page);
                throw errorInstance;
            });
        };

        const firstResponse = await fetchPage(1);
        const lastPage = isResponseWithMetaInRoot(firstResponse) ? firstResponse.lastPage : firstResponse.meta.lastPage;
        const allItems = [...firstResponse.data];
        options?.onProgress?.(1, lastPage, [...allItems]);

        const remainingPages = Array.from({ length: lastPage - 1 }, (_, index) => index + 2);
        const results = await Promise.all(remainingPages.map(fetchPage));
        for (const [index, result] of results.entries()) {
            allItems.push(...result.data);
            options?.onProgress?.(index + 2, lastPage, [...allItems]);
        }

        return allItems;
    };
}
